import axios from 'axios';
import { getItem, setItem, removeItem } from '@/lib/secureStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'smarthoops_auth_token';

// Auth-token helpers. Storage is the device Keychain on iOS / Android
// (via secureStore → capacitor-secure-storage-plugin) and localStorage
// on the web. The synchronous surface is preserved so existing call
// sites keep working — secureStore maintains an in-memory cache that
// `App.js` hydrates at boot.
export const setAuthToken = (token) => {
  if (token) {
    setItem(TOKEN_KEY, token);
  } else {
    removeItem(TOKEN_KEY);
  }
};

export const getAuthToken = () => {
  return getItem(TOKEN_KEY);
};

export const clearAuthToken = () => {
  removeItem(TOKEN_KEY);
};

// Create axios instance - Bearer tokens only (no cookies). The Kubernetes
// ingress strips Access-Control-Allow-Credentials and rewrites the
// Allow-Origin header to '*' on preflight responses, which causes
// WKWebView (iOS Capacitor) to reject any cross-origin request that has
// adds nothing and breaks the native app — keep it off.
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add Bearer token to all requests
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Cold-start resilience (build 201) ──────────────────────────────────
// The Emergent native deployment idles after periods of inactivity. The
// first request to a cold container hits a 502/503/504 from the ingress
// (or fails with a network error if the connection is dropped) while the
// FastAPI process boots and the Mongo pool hydrates. Cold-starts are
// transient — the SAME request retried 1-2 seconds later succeeds.
//
// We handle this transparently here so call sites don't all need their
// own retry logic:
//   1. RETRY ONCE on network errors + 502/503/504, with a 1.5s backoff.
//      Retries are safe for these specific signals because the request
//      either never reached the app (network/gateway error) OR the app
//      never finished booting (503). Either way, the request was NOT
//      processed, so retrying cannot create duplicate side-effects.
//      We deliberately do NOT retry 500s — those mean the app DID run
//      the request and an exception was raised. Retrying could double
//      a POST.
//   2. ATTACH `error.userMessage` on every rejection. Call sites can
//      pluck this via `apiErrorMessage(err, fallback)` to surface a
//      cold-start-friendly toast instead of a misleading action-specific
//      "Failed to create X" message. Real 4xx errors keep their
//      backend-supplied `detail` string.
const RETRY_DELAY_MS = 1500;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config || {};
    const status = error.response?.status;
    const hasResponse = !!error.response;
    const isNetwork = !hasResponse;           // request never made it back
    const isRetryableStatus = hasResponse && RETRYABLE_STATUSES.has(status);

    // ── Retry path ──
    // Only retry once per logical request. We tag the config so a second
    // failure on the retried request falls through to the normal reject.
    if ((isNetwork || isRetryableStatus) && !config.__retried) {
      config.__retried = true;
      await sleep(RETRY_DELAY_MS);
      try {
        return await api.request(config);
      } catch (retryErr) {
        // Fall through to classification using the retry's error so the
        // toast reflects the most recent failure.
        return Promise.reject(annotateError(retryErr));
      }
    }

    // ── 401 cleanup ──
    if (status === 401) {
      const token = getAuthToken();
      if (token) {
        console.log('Token may be expired, clearing...');
        clearAuthToken();
      }
    }

    console.error('API Error:', {
      message: error.message,
      status,
      data: error.response?.data,
      url: config.url,
    });
    return Promise.reject(annotateError(error));
  }
);

/**
 * Decorate an axios error with `userMessage` + `isServerCold` so call sites
 * can render a consistent, cold-start-friendly toast without each one
 * having to classify the error.
 */
function annotateError(error) {
  if (!error || error.userMessage) return error; // already annotated
  const status = error.response?.status;
  const hasResponse = !!error.response;
  if (!hasResponse) {
    error.isServerCold = true;
    error.userMessage = 'Getting things ready. Please try again in a moment.';
  } else if (status >= 500 && status < 600) {
    error.isServerCold = status !== 500; // 500 = app-level bug, not cold start
    error.userMessage =
      status === 500
        ? 'Something went wrong on our end. Please try again.'
        : 'Getting things ready. Please try again in a moment.';
  }
  return error;
}

/**
 * Centralized error→toast string resolver. Use anywhere we currently write
 *   `err.response?.data?.detail || 'Failed to X'`
 * Order of precedence:
 *   1. Backend-supplied `detail` (real 4xx errors with structured copy).
 *   2. Our cold-start / 5xx annotation (transient infra issues).
 *   3. The caller's action-specific fallback (e.g. 'Failed to start practice').
 */
export const apiErrorMessage = (err, fallback = 'Something went wrong') => {
  return err?.response?.data?.detail || err?.userMessage || fallback;
};

export default api;
export { BACKEND_URL };
